// Public viewer for a shared chart. No auth required — fetches
// /share/{token}, renders the stored chart_spec via the same
// ChartCard the chat uses (without the Add-to-dashboard / Share
// affordances).

import { useEffect, useState } from "react";
import { fetchShared, type SharedPayload } from "../../api/share";
import { ChartCard } from "../charts/ChartCard";

export function SharePage() {
  const token = window.location.pathname.replace(/^\/v2\/s\//, "").replace(/\/$/, "");
  const [data, setData] = useState<SharedPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Missing share token.");
      return;
    }
    fetchShared(token)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-red-700 px-6">
        Couldn't load this shared view. {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-line bg-white">
        <div className="max-w-3xl mx-auto px-6 h-[60px] flex items-center gap-3">
          <div className="h-7 w-7 rounded-full bg-brand text-white flex items-center justify-center font-bold text-sm">
            a
          </div>
          <div className="font-semibold tracking-tight text-neutral-900">Agami</div>
          <div className="ml-auto text-[12px] text-neutral-400">Shared view</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        {data.kind === "chart" ? (
          <>
            {data.user_query && (
              <div className="text-[14px] text-neutral-500">
                Shared answer to: <span className="text-neutral-800">{data.user_query}</span>
              </div>
            )}
            <ChartCard spec={data.chart_spec} />
          </>
        ) : (
          <>
            <div className="space-y-1">
              <h1 className="text-[18px] font-semibold text-neutral-900">{data.name}</h1>
              <div className="text-[12px] text-neutral-500">{data.organization}</div>
            </div>
            {data.insights.map((i, idx) => (
              <div key={idx} className="space-y-2">
                {i.user_query && (
                  <div className="text-[13px] text-neutral-500">{i.user_query}</div>
                )}
                <ChartCard spec={i.chart_spec} />
              </div>
            ))}
          </>
        )}
      </main>
    </div>
  );
}
